import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, FileSpreadsheet, Plug, PenLine, ArrowRight, Database } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useDemo, DemoHint } from "@/components/demo/DemoUi";
import { ImportWizard, ManualImport, ApiConnect } from "@/components/datahub/ImportDialogs";

const OptionCard = ({ icon: Icon, title, desc, onClick, disabled, badge, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`group relative flex flex-col items-start gap-4 rounded-2xl border border-slate-200 bg-white p-7 text-left transition-all ${
      disabled ? "cursor-not-allowed opacity-70" : "hover:border-[#3E7256] hover:shadow-sm"
    }`}
  >
    {badge && (
      <span className="absolute right-4 top-4 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {badge}
      </span>
    )}
    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ECF3EC] text-[#3E7256]">
      <Icon size={22} strokeWidth={1.6} />
    </span>
    <div>
      <h3 className="font-heading text-base font-semibold text-[#1E3D2B]">{title}</h3>
      <p className="mt-1 text-sm text-slate-500">{desc}</p>
    </div>
    {!disabled && (
      <span className="mt-1 flex items-center gap-1 text-xs font-semibold text-[#3E7256]">
        Commencer <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
      </span>
    )}
  </button>
);

export default function DataHub() {
  const { activeOrgId } = useAuth();
  const { readOnly } = useDemo();
  const [wizard, setWizard] = useState(null); // 'csv' | 'xlsx'
  const [manual, setManual] = useState(false);
  const [apiOpen, setApiOpen] = useState(false);

  return (
    <div className="space-y-10" data-testid="data-hub-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-3xl">Data Hub</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Importez vos données touristiques sans code. Choisissez une méthode d'import ci-dessous.
          </p>
        </div>
        <Link
          to="/data-sources"
          data-testid="view-data-sources"
          className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-[#1E3D2B] hover:border-[#3E7256]"
        >
          <Database size={16} /> Data Sources
        </Link>
      </div>

      {readOnly ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="datahub-readonly">
          <p className="text-sm font-semibold text-amber-900">Import indisponible</p>
          <DemoHint />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <OptionCard icon={FileText} title="Upload CSV" desc="Importez un fichier .csv"
            onClick={() => setWizard("csv")} testid="option-csv" />
          <OptionCard icon={FileSpreadsheet} title="Upload XLSX" desc="Importez un fichier Excel .xlsx"
            onClick={() => setWizard("xlsx")} testid="option-xlsx" />
          <OptionCard icon={Plug} title="API Connection" desc="Connectez une source externe (démo)"
            onClick={() => setApiOpen(true)} testid="option-api" />
          <OptionCard icon={PenLine} title="Manual Data" desc="Saisissez vos données à la main"
            onClick={() => setManual(true)} testid="option-manual" />
        </div>
      )}

      {/* CSV templates */}
      <div className="rounded-2xl border border-slate-200 bg-[#ECF3EC]/40 p-6">
        <h2 className="font-heading text-sm font-semibold text-[#1E3D2B]">Comment ça marche</h2>
        <p className="mt-1 text-sm text-slate-500">
          Importez votre fichier CSV ou Excel <span className="font-medium text-[#1E3D2B]">tel qu'il existe</span> — aucun
          format prédéfini n'est requis. Lodge & Co vérifie la sécurité du fichier, analyse sa structure et propose
          automatiquement le mapping : Import → Scan sécurité → Analyse du schéma → Mapping adaptatif → Validation → Import.
          La source, la date d'import, l'organisation, le type de données et la période couverte sont conservés, et vos
          données alimentent automatiquement les graphiques du tableau de bord.
        </p>
      </div>

      {wizard && (
        <ImportWizard
          open={!!wizard}
          kind={wizard}
          orgId={activeOrgId}
          onOpenChange={(v) => !v && setWizard(null)}
          onDone={() => {}}
        />
      )}
      <ManualImport open={manual} orgId={activeOrgId} onOpenChange={setManual} onDone={() => {}} />
      <ApiConnect open={apiOpen} orgId={activeOrgId} onOpenChange={setApiOpen} onDone={() => {}} />
    </div>
  );
}
